import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import React, { useState, useRef, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";

import { getAPIUrl } from "../../../config/api";
import { parseDate } from "../../../helpers/date";
import {
  useGetAllEdiConversionFilesQuery,
  useGetEdiConversionFileByIdQuery,
  useConvertAndUploadEdiMutation,
  useDeleteEdiConversionFileMutation,
} from "../../../store/queries/ediConversion";
import { useGetAllProvidersQuery } from "../../../store/queries/providers";
import { authSelector } from "../../../store/slices/auth";
import { userSelector } from "../../../store/slices/user";
import { IEdiConversionFile } from "../../../types/ediConversionFile";

const StyledDialog = styled(Dialog)`
  .p-dialog-content {
    padding: 1.5rem;
  }
`;

const FormField = styled.div`
  margin-bottom: 1rem;

  label {
    display: block;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #495057;
  }

  .required {
    color: #e74c3c;
    margin-left: 0.25rem;
  }
`;

const FileInfo = styled.div`
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 1rem;
  margin-top: 0.5rem;

  .file-name {
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .file-details {
    color: #6c757d;
    font-size: 0.875rem;
  }
`;

function EdiConversionFilesTable() {
  const toast = useRef<Toast>(null);
  const { token } = useSelector(authSelector);
  const { currentAccount } = useSelector(userSelector);

  const [uploadDialogVisible, setUploadDialogVisible] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [providerId, setProviderId] = useState<string>("");
  const [searchValue, setSearchValue] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [pollingFileId, setPollingFileId] = useState<string | null>(null);

  const { data: ediConversionFilesData, isLoading } =
    useGetAllEdiConversionFilesQuery({
      search: searchValue,
    });
  const { data: providers } = useGetAllProvidersQuery();
  const { data: pollingData } = useGetEdiConversionFileByIdQuery(
    pollingFileId!,
    {
      skip: !pollingFileId,
      pollingInterval: 1000, // Poll every 1 second
    }
  );
  const [convertAndUpload] = useConvertAndUploadEdiMutation();
  const [deleteFile] = useDeleteEdiConversionFileMutation();

  const ediConversionFiles = ediConversionFilesData?.data || [];

  const providerOptions = useMemo(() => {
    if (!providers?.data) return [];
    return providers.data.map((p: any) => ({ label: p.name, value: p.id }));
  }, [providers]);

  // Monitor polling data and stop when processing is complete
  useEffect(() => {
    if (!pollingData?.data || !pollingFileId) return;

    const status = pollingData.data.underpaymentProcessingStatus;
    const terminalStatuses = ["completed", "failed", "skipped"];

    if (status && terminalStatuses.includes(status)) {
      // Stop polling
      setPollingFileId(null);

      // Show appropriate toast message
      if (status === "completed") {
        toast.current?.show({
          severity: "success",
          summary: "Processing Complete",
          detail: "Underpayment records have been successfully created",
        });
      } else if (status === "failed") {
        toast.current?.show({
          severity: "error",
          summary: "Processing Failed",
          detail: "Failed to create underpayment records",
        });
      } else if (status === "skipped") {
        toast.current?.show({
          severity: "info",
          summary: "Processing Skipped",
          detail:
            "Underpayment processing was skipped (no provider ID specified)",
        });
      }
    }
  }, [pollingData, pollingFileId]);

  const resetForm = () => {
    setDisplayName("");
    setDescription("");
    setSelectedFile(null);
    setProviderId("");
  };

  const handleFileSelect = (event: any) => {
    const file = event.files[0] as File;
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!displayName.trim() || !selectedFile) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Please provide a display name and select a file",
      });
      return;
    }

    setIsUploading(true);
    try {
      const result = await convertAndUpload({
        file: selectedFile,
        displayName: displayName.trim(),
        description: description.trim() || undefined,
        providerId: providerId || undefined,
      }).unwrap();

      // Show initial toast indicating processing has started
      toast.current?.show({
        severity: "info",
        summary: "Processing Started",
        detail: "EDI file uploaded successfully. Processing underpayments...",
      });

      // Start polling the file status
      setPollingFileId(result.data.id);

      resetForm();
      setUploadDialogVisible(false);
    } catch (error: any) {
      console.error("Upload error:", error);
      toast.current?.show({
        severity: "error",
        summary: "Upload Failed",
        detail: error?.data?.message || "Failed to convert and upload EDI file",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async (id: string) => {
    try {
      const apiUrl = getAPIUrl();
      const headers: HeadersInit = {
        Authorization: `Bearer ${token}`,
      };

      if (currentAccount) {
        headers.account = currentAccount;
      }

      const response = await fetch(
        `${apiUrl}${
          apiUrl.endsWith("/") ? "" : "/"
        }edi-conversion/${id}/download`,
        {
          headers,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();

      // URL is nested in data.data.downloadUrl based on backend response structure
      const downloadUrl =
        data?.data?.downloadUrl || data?.downloadUrl || data?.url;

      if (downloadUrl) {
        window.open(downloadUrl, "_blank");
      } else {
        throw new Error("No download URL in response");
      }
    } catch (error: any) {
      console.error("Download error:", error);
      toast.current?.show({
        severity: "error",
        summary: "Download Failed",
        detail: error?.message || "Failed to download file",
      });
    }
  };

  const handleDelete = async (id: string, displayName: string) => {
    confirmDialog({
      message: `Are you sure you want to delete "${displayName}"?`,
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        try {
          await deleteFile(id).unwrap();
          toast.current?.show({
            severity: "success",
            summary: "Deleted",
            detail: "EDI conversion file deleted successfully",
          });
        } catch (error: any) {
          console.error("Delete error:", error);
          toast.current?.show({
            severity: "error",
            summary: "Delete Failed",
            detail: error?.data?.message || "Failed to delete file",
          });
        }
      },
    });
  };

  const dateBodyTemplate = (rowData: IEdiConversionFile) => {
    return parseDate(rowData.dateConverted);
  };

  const recordCountBodyTemplate = (rowData: IEdiConversionFile) => {
    return rowData.recordCount.toLocaleString();
  };

  const convertedByBodyTemplate = (rowData: IEdiConversionFile) => {
    // Handle if convertedBy is an object with name property
    if (
      typeof rowData.convertedBy === "object" &&
      rowData.convertedBy !== null
    ) {
      return (
        (rowData.convertedBy as any).name ||
        (rowData.convertedBy as any).email ||
        "Unknown"
      );
    }
    return rowData.convertedBy || "Unknown";
  };

  const providerBodyTemplate = (rowData: IEdiConversionFile) => {
    if (!rowData.providerId) {
      return <span className="text-500">-</span>;
    }

    // Find provider name from the list
    const provider = providers?.data?.find(
      (p: any) => p.id === rowData.providerId
    );
    return provider?.name || rowData.providerId;
  };

  const statusBodyTemplate = (rowData: IEdiConversionFile) => {
    const status = rowData.underpaymentProcessingStatus;
    const isPolling = pollingFileId === rowData.id;

    if (!status) {
      return <span className="text-500">-</span>;
    }

    switch (status) {
      case "pending":
        return (
          <span
            style={{
              color: "#6c757d",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            {isPolling && (
              <i
                className="pi pi-spin pi-spinner"
                style={{ fontSize: "1rem" }}
              />
            )}
            <span>Pending</span>
          </span>
        );
      case "processing":
        return (
          <span
            style={{
              color: "#007bff",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <i className="pi pi-spin pi-spinner" style={{ fontSize: "1rem" }} />
            <span>Processing</span>
          </span>
        );
      case "completed":
        return (
          <span style={{ color: "#28a745" }}>
            <i
              className="pi pi-check-circle"
              style={{ fontSize: "1rem", marginRight: "0.25rem" }}
            />
            Completed
          </span>
        );
      case "failed":
        return (
          <span style={{ color: "#dc3545" }}>
            <i
              className="pi pi-times-circle"
              style={{ fontSize: "1rem", marginRight: "0.25rem" }}
            />
            Failed
          </span>
        );
      case "skipped":
        return (
          <span style={{ color: "#ffc107" }}>
            <i
              className="pi pi-info-circle"
              style={{ fontSize: "1rem", marginRight: "0.25rem" }}
            />
            Skipped
          </span>
        );
      default:
        return <span>{status}</span>;
    }
  };

  const actionsBodyTemplate = (rowData: IEdiConversionFile) => {
    return (
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <Button
          icon="pi pi-download"
          className="p-button-rounded p-button-text"
          onClick={() => handleDownload(rowData.id)}
          tooltip="Download CSV"
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text p-button-danger"
          onClick={() => handleDelete(rowData.id, rowData.displayName)}
          tooltip="Delete"
        />
      </div>
    );
  };

  const dialogFooter = (
    <div>
      <Button
        label="Cancel"
        icon="pi pi-times"
        className="p-button-text"
        onClick={() => {
          resetForm();
          setUploadDialogVisible(false);
        }}
        disabled={isUploading}
      />
      <Button
        label={isUploading ? "Converting..." : "Convert & Upload"}
        icon={isUploading ? "pi pi-spin pi-spinner" : "pi pi-upload"}
        onClick={handleUpload}
        disabled={isUploading || !selectedFile || !displayName.trim()}
      />
    </div>
  );

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <Toast ref={toast} />

      <div className="flex justify-content-between align-items-center mb-3">
        <div className="flex align-items-center">
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            <InputText
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search files..."
              className="w-20rem"
            />
          </span>
        </div>
        <Button
          label="Upload & Convert EDI File"
          icon="pi pi-plus"
          className="p-button-raised"
          onClick={() => setUploadDialogVisible(true)}
        />
      </div>

      <DataTable
        value={ediConversionFiles}
        paginator
        rows={10}
        stripedRows
        responsiveLayout="scroll"
        emptyMessage="No EDI conversion files found."
      >
        <Column
          field="providerId"
          header="Provider"
          sortable
          body={providerBodyTemplate}
          style={{ minWidth: "10rem" }}
        />
        <Column
          field="displayName"
          header="Display Name"
          sortable
          style={{ minWidth: "12rem" }}
        />
        <Column
          field="fileName"
          header="File Name"
          sortable
          style={{ minWidth: "12rem" }}
        />
        <Column
          field="convertedBy"
          header="Converted By"
          sortable
          body={convertedByBodyTemplate}
          style={{ minWidth: "10rem" }}
        />
        <Column
          field="dateConverted"
          header="Date Converted"
          sortable
          body={dateBodyTemplate}
          style={{ minWidth: "10rem" }}
        />
        <Column
          field="recordCount"
          header="Record Count"
          sortable
          body={recordCountBodyTemplate}
          style={{ minWidth: "8rem" }}
        />
        <Column
          field="underpaymentProcessingStatus"
          header="Status"
          sortable
          body={statusBodyTemplate}
          style={{ minWidth: "10rem" }}
        />
        <Column
          body={actionsBodyTemplate}
          header="Actions"
          style={{ minWidth: "8rem" }}
        />
      </DataTable>

      <StyledDialog
        header="Upload & Convert EDI File"
        visible={uploadDialogVisible}
        onHide={() => {
          if (!isUploading) {
            resetForm();
            setUploadDialogVisible(false);
          }
        }}
        footer={dialogFooter}
        style={{ width: "500px" }}
        modal
        closable={!isUploading}
      >
        <FormField>
          <label>
            Display Name <span className="required">*</span>
          </label>
          <InputText
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g., Bluecross Claims Batch A"
            className="w-full"
            disabled={isUploading}
          />
        </FormField>

        <FormField>
          <label>Description</label>
          <InputTextarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description..."
            rows={3}
            className="w-full"
            disabled={isUploading}
          />
        </FormField>

        <FormField className="mb-4">
          <label>Provider</label>
          <Dropdown
            value={providerId}
            options={providerOptions}
            onChange={(e) => setProviderId(e.value)}
            placeholder="Select Provider (Optional)"
            className="w-full"
            filter
            showClear
            disabled={isUploading}
          />
        </FormField>

        <FormField>
          <label>
            EDI File (.txt, .edi, .835) <span className="required">*</span>
          </label>
          {selectedFile ? (
            <FileInfo>
              <div className="file-name">{selectedFile.name}</div>
              <div className="file-details">
                Size: {(selectedFile.size / 1024).toFixed(2)} KB
                <Button
                  icon="pi pi-times"
                  className="p-button-text p-button-sm ml-2"
                  onClick={() => setSelectedFile(null)}
                  disabled={isUploading}
                  tooltip="Remove file"
                />
              </div>
            </FileInfo>
          ) : (
            <FileUpload
              mode="basic"
              name="file"
              accept=".txt,.edi,.835"
              maxFileSize={10000000} // 10MB
              chooseLabel="Choose EDI File"
              className="w-full"
              onSelect={handleFileSelect}
              disabled={isUploading}
            />
          )}
          <small className="text-muted">
            Supported formats: .txt, .edi, .835 (Max size: 10MB)
          </small>
        </FormField>
      </StyledDialog>
    </>
  );
}

export default EdiConversionFilesTable;
